import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import CountBox from "@/components/ui/CountBox";
import { useMemo, useState, useEffect, useCallback } from "react";
import { Footer } from "@/components/layout/Footer";
import Table, { TableColumn } from "@/components/ui/Table";
import { formatNumber, sumBy } from "@/lib/utils";
import DateBox from "@/components/ui/DateBox";
import { useHazardous } from "@/hooks/management/mg_hazardous/useHazardous";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import SelectBox from "@/components/ui/SelectBox";
import { getToday } from "@/lib/date";

export default function SalesByClientPage() {
  const [searchValue, setSearchValue] = useState("");
  const [actualSearchValue, setActualSearchValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rangeBtn, setRangeBtn] = useState("today");
  const [tableVer, setTableVer] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [allData, setAllData] = useState<any[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchType, setSearchType] = useState("itemName");
  const [searchActualType, setSearchActualType] = useState("itemName");
  const [exactMatch, setExactMatch] = useState(false);
  const [actualExactMatch, setActualExactMatch] = useState(false);

  const {
    data: hazardousData,
    isLoading,
  } = useHazardous({
    searchType: searchActualType,
    search: actualSearchValue,
    dateFrom: startDate,
    dateTo: endDate,
    exactMatch: actualExactMatch,
    page: currentPage,
    size: 20,
  });

  useEffect(() => {
    const today = getToday();
    setStartDate(today);
    setEndDate(today);
  }, []);

  // 날짜 변경 시 실시간 조회
  useEffect(() => {
    if (startDate && endDate) {
      setCurrentPage(0);
      setTotalElements(0);
      setAllData([]);
      setLoadingMore(false);
      setTableVer(v => v + 1);
    }
  }, [startDate, endDate]);

  const handleReset = () => {
    setSearchType("itemName");
    setSearchValue("");
    setExactMatch(false);
    const today = getToday();
    setActualSearchValue("");
    setActualExactMatch(false);
    setStartDate(today);
    setEndDate(today);
    setCurrentPage(0);
    setTotalElements(0);
    setRangeBtn("today");
    setLoadingMore(false);
    setAllData([]);
    setTableVer(v => v + 1);
  };

  const handleSearch = useCallback(() => {
    setActualSearchValue(searchValue);
    setSearchActualType(searchType);
    setActualExactMatch(exactMatch);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
    setAllData([]);
    setTableVer(v => v + 1);
  }, [searchValue, searchType, exactMatch]);

  // 테이블 컬럼
  const columns: TableColumn[] = [
    { key: "stItemName", header: "품목명", bodyAlign: "left" },
    { key: "saleSlipDate", header: "등록일자", bodyAlign: "center" },
    { key: "saleSlipInQty", header: "운반량", bodyAlign: "center", render: (v: number) => formatNumber(v) },
    { key: "saleSlipInLocation", header: "출발지점", bodyAlign: "center" },
    { key: "saleSlipOutArea1", header: "도착지점", bodyAlign: "center" },
    { key: "stVehicleNumber2", header: "차량번호", bodyAlign: "left" },
    { key: "stCompanyNameShort", header: "운송사명", bodyAlign: "left" },
    { key: "stCompanyNameShort2", header: "상호", bodyAlign: "left", group: "위탁인" },
    { key: "stCompanyAddress", header: "주소", bodyAlign: "left", group: "위탁인" },
    { key: "stCompanyTel", header: "전화번호", bodyAlign: "left", group: "위탁인" },
    { key: "stCompanyNumber", header: "사업자 번호", bodyAlign: "left", group: "위탁인" },
    { key: "saleSlipRemark", header: "비고", bodyAlign: "left" },
  ];

  // Footer
  const footerGrid = useMemo(() => {
    const totalQty = sumBy(allData, 'saleSlipInQty');
    const totalSales = sumBy(allData, 'saleSlipSaleSupportAmt');
    const totalPurchase = sumBy(allData, 'saleSlipBuySupportAmt');
    const totalMargin = sumBy(allData, 'saleSlipProfitAmt');

    return [[
      { label: "운반량", value: formatNumber(totalQty) },
      { label: "매출액", value: formatNumber(totalSales) },
      { label: "매입액", value: formatNumber(totalPurchase) },
      { label: "운송마진", value: formatNumber(totalMargin) },
    ]];
  }, [allData]);

  // 검색어가 변경될 때 페이지 초기화
  useEffect(() => {
    setAllData([]);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
    setTableVer(v => v + 1);
  }, [actualSearchValue, actualExactMatch]);

  // 새로운 페이지 데이터가 로드되면 누적 데이터에 추가
  useEffect(() => {
    if (hazardousData && hazardousData.content && hazardousData.content.length > 0) {
      if (currentPage === 0) {
        setAllData(hazardousData.content);
        setTotalElements(hazardousData.totalElements || 0);
      } else {
        setAllData(prev => [...prev, ...hazardousData.content]);
      }
    } else if (hazardousData && (!hazardousData.content || hazardousData.content.length === 0)) {
      setAllData([]);
      setTotalElements(0);
    }
  }, [hazardousData, currentPage]);

  useEffect(() => {
    if (hazardousData) {
      setLoadingMore(false);
    }
  }, [hazardousData]);

  const hasMore = useMemo(() => {
    if (!hazardousData) return false;
    return !hazardousData.last && allData.length < totalElements;
  }, [hazardousData?.last, allData.length, totalElements]);

  const handleTableLoadMore = useCallback(
    async (currentCount: number) => {
      if (!hasMore || loadingMore) {
        return;
      }

      if (currentCount >= allData.length * 0.8) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
    [hasMore, loadingMore, allData.length]
  );

  const handleDateSelect = (range: { startDate: string; endDate: string }) => {
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  return (
    <div className="flex flex-col h-full">
      {/* 검색영역 */}
      <SearchBoxLayout>
        <div className="flex items-end">
          <SelectBox
            label="검색어"
            className="w-25"
            value={searchType}
            onChange={(v) => setSearchType(v as "stItemName" | "stItemCode")}
            options={[
              { label: "품목명", value: "itemName" },
              { label: "출발지점", value: "saleSlipOutArea1" },
              { label: "도착지점", value: "saleSlipOutArea2" },
              { label: "차량번호", value: "stVehicleNumber2" },
              { label: "운송사명", value: "stCompanyNameShort" }
            ]}
          />
          <div className="mr-1" />
          <InputBox
            label="검색어"
            className="w-50"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <div className="mr-3" />
          <DateBox
            label="등록일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-2" />
          <DateSearchBtn
            mode="range"
            value={rangeBtn}
            onChangeValue={setRangeBtn}
            currentRange={{ startDate, endDate }}
            onDateSelect={handleDateSelect}
          />
          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>초기화</Button>
            <Button variant="search" onClick={handleSearch}>조회</Button>
          </div>
        </div>
      </SearchBoxLayout>

      {/* 상단 버튼/카운트 */}
      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={allData.length} />
          <div className="flex gap-2">
            <PrintButton data={allData} columns={columns} title="유해화학물질관리대장" skipPermissionCheck />
            <ExcelButton data={allData} columns={columns} fileName="유해화학물질관리대장" skipPermissionCheck />
          </div>
        </div>

        {/* 본문 표 */}
        <div className="flex-1 min-h-0">
          <Table
            key={`hazardous-table-v${tableVer}`}
            data={allData}
            className="h-full"
            infiniteScroll={true}
            initialLoadCount={20}
            loadMoreCount={20}
            hasMore={hasMore}
            onLoadMore={handleTableLoadMore}
            loadingMore={loadingMore}
            columns={columns}
            loading={isLoading && allData.length === 0}
            showCheckbox={true}
            showRowNumber={true}
            maxHeight="100%"
          />
        </div>
        {isLoading && allData.length > 0 && <div className="text-center py-2 text-gray-500">새 페이지 로딩 중...</div>}
      </main>

      {/* 푸터 총계 */}
      <footer className="flex-shrink-0 p-4 bg-gray-50 border-t">
        <Footer grid={footerGrid} />
      </footer>
    </div>
  );
}
